import { useState, useMemo, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Search, ChevronDown, Play, Pause, Mic, Loader2 } from 'lucide-react';
import { C } from '../../styles/tokens';
import { useLanguages } from '../../http-hooks/ai-voices/useLanguages';
import { useClonedVoices } from '../../http-hooks/ai-voices/useClonedVoice';
import { useSavedProfiles } from '../../http-hooks/saved-profiles/useSavedProfiles';
import { useAudioPreview } from '../../http-hooks/ai-voices/useAudioPreview';
import { usePreGenAudioPlayer } from '../../zustand-store/usePreGenAudioPlayerStore';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';
import { getVoicePreviewUrl } from '../../utils/voicePreview';
import { languageNameFromCode } from '../../utils/languageDisplay';
import { buildLanguageGroups, buildLanguageOptions, buildAccentOptions } from '../../utils/languageGrouping';

const AVATAR_COLORS = [
    { bg: '#EDE9FE', color: '#6D28D9' },
    { bg: '#FEF3C7', color: '#92400E' },
    { bg: '#D1FAE5', color: '#065F46' },
    { bg: '#DBEAFE', color: '#1E40AF' },
    { bg: '#FCE7F3', color: '#831843' },
    { bg: '#FEE2E2', color: '#991B1B' },
    { bg: '#CCFBF1', color: '#0F766E' },
    { bg: '#FFF7ED', color: '#C2410C' },
];

const ENGINE_LABEL = { emotional: 'Emotional', neural: 'Premium', standard: 'Standard' };
const ENGINE_COLOR = { emotional: '#7C3AED', neural: '#B45309', standard: '#64748B' };


// ── Shared empty state ─────────────────────────────────────────────────────────
function Empty({ label }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 8, padding: 24, color: C.n400 }}>
            <Mic size={24} strokeWidth={1.5} />
            <span style={{ fontSize: 13 }}>{label}</span>
        </div>
    );
}

// ── Compact voice row ──────────────────────────────────────────────────────────
function VoiceRow({ voice, index, isSelected, onSelect, instanceKey }) {
    const { activeKey, isPlaying: globalIsPlaying, loadingSrc, togglePlayPause } = usePreGenAudioPlayer();
    const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
    const genderLabel = voice.gender && voice.gender !== 'neutral'
        ? voice.gender.charAt(0).toUpperCase() + voice.gender.slice(1)
        : '';
    const audioUrl = getVoicePreviewUrl(voice);
    const isPlaying = globalIsPlaying && activeKey === instanceKey;
    const isLoading = activeKey === instanceKey && !!loadingSrc;

    const handlePlay = (e) => {
        e.stopPropagation();
        if (!audioUrl || isLoading) return;
        togglePlayPause(audioUrl, instanceKey);
    };

    return (
        <div
            onClick={() => onSelect(voice)}
            style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 14px', borderBottom: `1px solid ${C.n100}`,
                background: isSelected ? C.p50 : 'transparent',
                cursor: 'pointer', transition: 'background .1s',
            }}
            onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = C.n50; }}
            onMouseLeave={e => { e.currentTarget.style.background = isSelected ? C.p50 : 'transparent'; }}
        >
            <div style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                background: color.bg, color: color.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700,
            }}>
                {voice.name[0]}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.n900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {voice.name}
                </div>
                <div style={{ fontSize: 11, color: C.n500, marginTop: 1 }}>
                    {genderLabel && <span>{genderLabel}</span>}
                    {genderLabel && voice.engine && <span style={{ margin: '0 3px' }}>·</span>}
                    {voice.engine && (
                        <span style={{ color: ENGINE_COLOR[voice.engine] || C.n500, fontWeight: 600 }}>
                            {ENGINE_LABEL[voice.engine] || voice.engine}
                        </span>
                    )}
                </div>
            </div>

            <button
                type="button"
                onClick={handlePlay}
                disabled={!audioUrl || isLoading}
                title={isLoading ? 'Loading…' : isPlaying ? 'Pause' : 'Preview'}
                style={{
                    width: 28, height: 28, borderRadius: '50%',
                    border: `1px solid ${isPlaying ? C.p600 : C.n200}`,
                    background: isPlaying ? C.p50 : C.w,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: audioUrl && !isLoading ? 'pointer' : 'not-allowed', flexShrink: 0,
                    color: isPlaying ? C.p600 : C.n500,
                }}
            >
                {isLoading
                    ? <Loader2 size={11} className="vi-spin" />
                    : isPlaying ? <Pause size={11} /> : <Play size={11} style={{ marginLeft: 1 }} />}
            </button>

            <button
                type="button"
                onClick={e => { e.stopPropagation(); onSelect(voice); }}
                style={{
                    height: 28, padding: '0 10px', borderRadius: 7,
                    border: `1.5px solid ${isSelected ? C.p600 : C.n200}`,
                    background: isSelected ? C.p600 : C.w,
                    color: isSelected ? C.w : C.n700,
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', flexShrink: 0,
                    fontFamily: 'inherit', transition: 'all .15s',
                }}
            >
                {isSelected ? 'Selected' : 'Use'}
            </button>
        </div>
    );
}

// ── Library tab ────────────────────────────────────────────────────────────────
function LibraryTab({ onVoiceSelect, selectedVoice, emotionalOnly }) {
    const { data: languages, isLoading } = useLanguages(undefined, { legacy: false });

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedLangPrefix, setSelectedLangPrefix] = useState('en');
    const [selectedLangCode, setSelectedLangCode] = useState('en-US');
    const [gender, setGender] = useState('all');
    const [quality, setQuality] = useState('all');

    const langGroups = useMemo(() => buildLanguageGroups(languages), [languages]);

    const languageOptions = useMemo(() => buildLanguageOptions(langGroups), [langGroups]);

    const accentOptions = useMemo(() => buildAccentOptions(langGroups, selectedLangPrefix), [selectedLangPrefix, langGroups]);

    const filteredVoices = useMemo(() => {
        if (!languages) return [];
        let voices = [];
        if (selectedLangCode && languages[selectedLangCode]) {
            voices = languages[selectedLangCode].voices || [];
        } else if (selectedLangPrefix && langGroups[selectedLangPrefix]) {
            voices = langGroups[selectedLangPrefix].flatMap(l => l.voices || []);
        }
        if (emotionalOnly) voices = voices.filter(v => v.engine === 'emotional');
        if (gender !== 'all') voices = voices.filter(v => v.gender === gender);
        if (!emotionalOnly && quality !== 'all') voices = voices.filter(v => v.engine === quality);
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            voices = voices.filter(v => v.name.toLowerCase().includes(q));
        }
        return voices;
    }, [languages, selectedLangCode, selectedLangPrefix, langGroups, emotionalOnly, gender, quality, searchQuery]);

    const langName = selectedLangCode
        ? languages?.[selectedLangCode]?.name
        : (selectedLangPrefix ? languageNameFromCode(selectedLangPrefix) : '');

    if (isLoading) {
        return (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 13, color: C.n400 }}>Loading voices…</span>
            </div>
        );
    }

    const selectStyle = {
        height: 32, paddingLeft: 10, paddingRight: 24,
        border: `1px solid ${C.n200}`, borderRadius: 7,
        background: C.w, fontSize: 12, color: C.n700,
        outline: 'none', cursor: 'pointer',
        appearance: 'none', WebkitAppearance: 'none',
        fontFamily: 'inherit',
    };

    return (
        <>
            {/* Search */}
            <div style={{ position: 'relative', padding: '10px 14px 0', flexShrink: 0 }}>
                <Search size={14} style={{ position: 'absolute', left: 26, top: '50%', transform: 'translateY(-30%)', color: C.n400, pointerEvents: 'none' }} />
                <input
                    type="text"
                    placeholder="Search voices…"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{
                        width: '100%', boxSizing: 'border-box',
                        paddingLeft: 34, paddingRight: 10, height: 34,
                        border: `1px solid ${C.n200}`, borderRadius: 8,
                        fontSize: 13, color: C.n900, background: C.w,
                        outline: 'none',
                    }}
                />
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 6, padding: '8px 14px', flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Language */}
                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                    <select value={selectedLangPrefix} onChange={e => { setSelectedLangPrefix(e.target.value); setSelectedLangCode(''); }} style={{ ...selectStyle, background: C.p600, color: C.w, border: 'none', paddingRight: 22 }}>
                        {languageOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <ChevronDown size={12} color={C.w} style={{ position: 'absolute', right: 6, pointerEvents: 'none' }} />
                </div>

                {/* Accent */}
                {accentOptions.length > 0 && (
                    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                        <select value={selectedLangCode} onChange={e => setSelectedLangCode(e.target.value)} style={selectStyle}>
                            <option value="">All accents</option>
                            {accentOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <ChevronDown size={12} color={C.n400} style={{ position: 'absolute', right: 6, pointerEvents: 'none' }} />
                    </div>
                )}

                {/* Gender pills */}
                {['all', 'male', 'female'].map(g => (
                    <button
                        key={g}
                        type="button"
                        onClick={() => setGender(g)}
                        style={{
                            height: 32, padding: '0 10px', borderRadius: 7,
                            border: `1px solid ${gender === g ? C.p600 : C.n200}`,
                            background: gender === g ? C.p50 : C.w,
                            color: gender === g ? C.p600 : C.n600,
                            fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            fontFamily: 'inherit',
                        }}
                    >
                        {g === 'all' ? 'All' : g === 'male' ? 'M' : 'F'}
                    </button>
                ))}

                {/* Quality — hidden when emotionalOnly */}
                {!emotionalOnly && (
                    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                        <select value={quality} onChange={e => setQuality(e.target.value)} style={selectStyle}>
                            <option value="all">Quality</option>
                            <option value="emotional">Emotional</option>
                            <option value="neural">Premium</option>
                            <option value="standard">Standard</option>
                        </select>
                        <ChevronDown size={12} color={C.n400} style={{ position: 'absolute', right: 6, pointerEvents: 'none' }} />
                    </div>
                )}
            </div>

            {/* Count */}
            <div style={{ padding: '2px 14px 6px', fontSize: 12, color: C.n400, fontWeight: 500, flexShrink: 0 }}>
                {filteredVoices.length} voices{langName ? ` · ${langName}` : ''}
            </div>

            {/* Voice list */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                {filteredVoices.length === 0
                    ? <Empty label="No voices found" />
                    : filteredVoices.map((voice, i) => (
                        <VoiceRow
                            key={`${voice.code}-${voice.engine}`}
                            voice={voice}
                            index={i}
                            isSelected={selectedVoice?.code === voice.code && selectedVoice?.engine === voice.engine}
                            onSelect={onVoiceSelect}
                            instanceKey={`library:${voice.code}-${voice.engine}`}
                        />
                    ))
                }
            </div>
        </>
    );
}

// ── Cloned tab ─────────────────────────────────────────────────────────────────
function ClonedTab({ onVoiceSelect, selectedVoice }) {
    const { org_id, id } = useParams();
    const { data: allVoices = [], isLoading, error } = useClonedVoices({ org_id, integration_id: id, pollWhileProcessing: true });
    // Design/mix outputs live in the same list — exclude them here since this tab is for
    // picking an existing cloned voice as a reference/source, not chaining design-of-a-design.
  
    const clonedVoices = allVoices.filter(cv =>
        (cv.type === 'cloned' || !cv.type) && (!cv.status || cv.status === 'ready')
    );

    if (isLoading) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: 13, color: C.n400 }}>Loading…</span></div>;
    if (error) return <Empty label="Failed to load cloned voices" />;
    if (clonedVoices.length === 0) return <Empty label="No cloned voices yet" />;

    const voices = clonedVoices.map(cv => ({
        code: cv.id, name: cv.voice_name,
        engine: 'emotional', gender: 'neutral',
        audio: cv.audio_sample_path,
        // emotion_files.neutral (from /prepare_clone) is this voice's own generated
        // neutral reference clip — the correct thing to preview. vox_audio_path is a
        // legacy fallback from the old /clone-based instant-clone flow (now dead);
        // audio_sample_path is the raw upload, used only as a last resort.
        previewAudio: cv.emotion_files?.neutral || cv.vox_audio_path || cv.audio_sample_path,
        styles: [],
        metadata: { isCloned: true, emotion_files: cv.emotion_files || null },
    }));

    return (
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {voices.map((voice, i) => (
                <VoiceRow
                    key={voice.code}
                    voice={voice}
                    index={i}
                    isSelected={selectedVoice?.code === voice.code}
                    onSelect={onVoiceSelect}
                    instanceKey={`cloned:${voice.code}`}
                />
            ))}
        </div>
    );
}

// ── Saved profiles tab ─────────────────────────────────────────────────────────
function SavedTab({ onProfileSelect, selectedVoice }) {
    const { org_id, id } = useParams();
    const { data: profilesResponse, isLoading } = useSavedProfiles({ org_id, id });
    const { data: languages } = useLanguages(undefined, { legacy: false });
    const { mutateAsync: generatePreview } = useAudioPreview();
    const { activeKey, isPlaying: globalIsPlaying, loadingSrc, togglePlayPause } = usePreGenAudioPlayer();
    const [pendingId, setPendingId] = useState(null);
    // Caches the generated preview URL per profile so replaying doesn't re-hit the API.
    const previewUrlCache = useRef({});
    const savedProfiles = profilesResponse?.data || [];

    if (isLoading) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: 13, color: C.n400 }}>Loading…</span></div>;
    if (savedProfiles.length === 0) return <Empty label="No saved profiles yet" />;

    const handlePlay = async (e, profile) => {
        e.stopPropagation();
        const key = `saved:${profile.id}`;
        const cachedUrl = previewUrlCache.current[profile.id];
        if (cachedUrl) {
            togglePlayPause(cachedUrl, key);
            return;
        }
        setPendingId(profile.id);
        try {
            const resp = await generatePreview({ profile });
            previewUrlCache.current[profile.id] = resp.data.audio_url;
            togglePlayPause(resp.data.audio_url, key);
        } catch (err) {
            Sentry.captureException(err);
            toast.error("Couldn't preview this profile");
        } finally {
            setPendingId(null);
        }
    };

    return (
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {savedProfiles.map((profile, i) => {
                const lang = languages?.[profile.lang_code];
                const profileVoice = lang?.voices?.find(v => v.code === profile.voice && v.engine === profile.engine);
                const color = AVATAR_COLORS[i % AVATAR_COLORS.length];
                const instanceKey = `saved:${profile.id}`;
                const isPlaying = globalIsPlaying && activeKey === instanceKey;
                const isLoadingAudio = pendingId === profile.id || (activeKey === instanceKey && !!loadingSrc);

                return (
                    <div
                        key={profile.id || i}
                        onClick={() => onProfileSelect(profile, profileVoice)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '9px 14px', borderBottom: `1px solid ${C.n100}`,
                            cursor: 'pointer', transition: 'background .1s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = C.n50; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                        <div style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, background: color.bg, color: color.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 }}>
                            {profile.profile_name[0]}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.n900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {profile.profile_name}
                            </div>
                            <div style={{ fontSize: 11, color: C.n500, marginTop: 1 }}>
                                {lang?.name || profile.lang_code}{profileVoice ? ` · ${profileVoice.name}` : ''}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={e => handlePlay(e, profile)}
                            disabled={isLoadingAudio}
                            title={isLoadingAudio ? 'Loading…' : isPlaying ? 'Pause' : 'Preview'}
                            style={{ width: 28, height: 28, borderRadius: '50%', border: `1px solid ${isPlaying ? C.p600 : C.n200}`, background: isPlaying ? C.p50 : C.w, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: isLoadingAudio ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                        >
                            {isLoadingAudio
                                ? <Loader2 size={11} color={C.n500} className="vi-spin" />
                                : isPlaying ? <Pause size={11} color={C.p600} /> : <Play size={11} color={C.n500} style={{ marginLeft: 1 }} />}
                        </button>
                        <button
                            type="button"
                            onClick={e => { e.stopPropagation(); onProfileSelect(profile, profileVoice); }}
                            style={{ height: 28, padding: '0 10px', borderRadius: 7, border: `1.5px solid ${C.n200}`, background: C.w, color: C.n700, fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' }}
                        >
                            Use
                        </button>
                    </div>
                );
            })}
        </div>
    );
}

// ── EmbeddedVoicePicker ────────────────────────────────────────────────────────

const TABS = [
    { id: 'library', label: 'Library' },
    { id: 'cloned', label: 'Cloned' },
    { id: 'saved', label: 'Saved' },
];

const PICKER_KEY_PREFIXES = ['library:', 'cloned:', 'saved:'];

export default function EmbeddedVoicePicker({ onVoiceSelect, emotionalOnly = false, selectedVoice = null }) {
    const [activeTab, setActiveTab] = useState('library');

    // This picker is usually shown in a dropdown/popover and unmounts on close — stop any
    // preview it started so audio doesn't keep playing silently after the picker is dismissed.
    // Only stop if the currently-active audio actually came from this picker (key prefix check),
    // so we don't interrupt a preview playing elsewhere in the app.
    useEffect(() => () => {
        const { activeKey, stop } = usePreGenAudioPlayer.getState();
        if (activeKey && PICKER_KEY_PREFIXES.some(prefix => activeKey.startsWith(prefix))) {
            stop();
        }
    }, []);

    const tabStyle = (id) => ({
        flex: 1, padding: '10px 0', fontSize: 13, fontWeight: 600,
        border: 'none', background: 'transparent', cursor: 'pointer',
        color: activeTab === id ? C.p600 : C.n400,
        borderBottom: `2px solid ${activeTab === id ? C.p600 : 'transparent'}`,
        transition: 'all .15s', fontFamily: 'inherit',
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {/* Tab bar */}
            <div style={{ display: 'flex', borderBottom: `1px solid ${C.n200}`, flexShrink: 0 }}>
                {TABS.map(t => (
                    <button key={t.id} type="button" style={tabStyle(t.id)} onClick={() => setActiveTab(t.id)}>
                        {t.label}
                    </button>
                ))}
            </div>

            {activeTab === 'library' && (
                <LibraryTab
                    onVoiceSelect={onVoiceSelect}
                    selectedVoice={selectedVoice}
                    emotionalOnly={emotionalOnly}
                />
            )}
            {activeTab === 'cloned' && (
                <ClonedTab
                    onVoiceSelect={onVoiceSelect}
                    selectedVoice={selectedVoice}
                />
            )}
            {activeTab === 'saved' && (
                <SavedTab
                    onProfileSelect={(profile, voice) => onVoiceSelect(voice || { name: profile.profile_name, code: profile.voice, engine: profile.engine })}
                    selectedVoice={selectedVoice}
                />
            )}
        </div>
    );
}
